// Interfaces principales
export interface DeclaracionJurada {
  datosPersonales: DatosPersonales;
  antecedentesMedicos: AntecedentesMedicos;
  habitosFisiologicos: HabitosFisiologicos;
  habitosToxicos: HabitosToxicos;
  antecedentesGinecoObstetricos: AntecedentesGinecoObstetricos;
  antecedentesFamiliares: AntecedentesFamiliares;
  antecedentesPatologicos: AntecedentesPatologicos;
  sintomasYExperiencias: SintomasYExperiencias;
  antecedentesQuirurgicos: AntecedentesQuirurgicos;
  // declaracionAceptacion: boolean;
  fechaDeclaracion: Date;
}

// Datos personales
export interface DatosPersonales {
  nombre: string;
  apellido: string;
  fechaNacimiento: Date;
  dni: string;
  domicilio: string;
  localidad: string;
  telefono: string;
  empleador: string;
  cuit: string;
  grupoSanguineo: GrupoSanguineo;
  factorRh: boolean; // true = Positivo (+), false = Negativo (-)
  otros?: string;
}

export enum GrupoSanguineo {
  A = 'A',
  B = 'B',
  AB = 'AB',
  O = 'O' // es una o
}

// Antecedentes médicos
export interface AntecedentesMedicos {
  atencionMedica: {
    bajoAtencion: boolean;
    medicoAtiende?: string;
    enfermedad?: string;
    cualEnfermedad?: string;
  };
  medicamentosUltimos6Meses?: string;
  motivoConsultaActual: boolean;
}

// Hábitos fisiológicos
export interface HabitosFisiologicos {
  alimentacion: {
    comidasDiarias: boolean;
    queAlimentacion?: string;
    incluyeDesayuno: boolean;
  };
  sueno: {
    suenoTranquilo: boolean;
    horasSueno?: string;
  };
  constipacion: boolean;
  deporte: {
    realizaDeporte: boolean;
    cualDeporte?: string;
  };
}

// Hábitos tóxicos
export interface HabitosToxicos {
  fumador: {
    fuma: boolean;
    desdeCuando?: string;
    cantidadPorDia?: string;
  };
  alcohol: {
    tomaAlcohol: boolean;
    cuales?: string;
    cuando?: string;
    cuanto?: string;
  };
  drogas: {
    consumeDrogas: boolean;
    cuales?: string;
  };
  automedicacion: {
    seAutomedica: boolean;
    medicacion?: string;
    frecuencia?: string;
  };
}

// Antecedentes gineco-obstétricos
export interface AntecedentesGinecoObstetricos {
  alteracionesMenstruales: {
    tiene: boolean;
    primeraMenstruacionEdad?: string;
    ultimaMenstruacionFecha?: string;
  };
  embarazos: {
    tuvo: boolean;
    cantidad?: string;
    partoNormal?: string;
    cesareas?: string;
    abortos?: string;
  };
  patologiaMamaria: {
    tiene: boolean;
    cuales?: string;
  };
}

// Interfaces para antecedentes familiares y patológicos
export interface AntecedenteFamiliar {
  tiene: boolean;
  quien?: string;
  cuales?: string;
}

export interface AntecedentePatologico {
  tiene: boolean;
  cual?: string;
}

export interface Sintoma {
  tiene: boolean;
  cual?: string;
}

// Antecedentes familiares
export interface AntecedentesFamiliares {
  artritisFamiliar: AntecedenteFamiliar;
  enfermedadesInmunologicas: AntecedenteFamiliar;
  cancer: AntecedenteFamiliar;
  enfermedadCardiaca: AntecedenteFamiliar;
  tensionAlta: AntecedenteFamiliar;
  enfermedadRenal: AntecedenteFamiliar;
  tuberculosis: AntecedenteFamiliar;
  enfermedadesMentales: AntecedenteFamiliar;
  enfermedadesHereditariasGeneticas: AntecedenteFamiliar;
}

// Antecedentes patológicos
export interface AntecedentesPatologicos {
  diabetes: AntecedentePatologico;
  hipertension: AntecedentePatologico;
  enfermedadCardiaca: AntecedentePatologico;
  alergia: AntecedentePatologico;
  asma: AntecedentePatologico;
  tuberculosis: AntecedentePatologico;
  calculosRenales: AntecedentePatologico;
  infeccionUrinaria: AntecedentePatologico;
  hepatitis: AntecedentePatologico;
  chagas: AntecedentePatologico;
  brucelosis: AntecedentePatologico;
  toxoplasmosis: AntecedentePatologico;
  trastornosPsiquiatricos: AntecedentePatologico;
  trastornosMarcha: AntecedentePatologico;
  dispositivosMedicos: AntecedentePatologico;
  enfermedadesTransmisionSexual: AntecedentePatologico;
  alteracionesEndocrinas: AntecedentePatologico;
  artritis: AntecedentePatologico;
  enfermedadesPiel: AntecedentePatologico;
  epilepsia: AntecedentePatologico;
  ulceraGastrica: AntecedentePatologico;
  gastritis: AntecedentePatologico;
  hemorroides: AntecedentePatologico;
  hernias: AntecedentePatologico;
  sinusitis: AntecedentePatologico;
  cancerTumores: AntecedentePatologico;
  varices: AntecedentePatologico;
  discapacidad: AntecedentePatologico;
  incapacidad: AntecedentePatologico;
  medicoEspecialTraslado: AntecedentePatologico;
}

// Síntomas y experiencias
export interface SintomasYExperiencias {
  mareosDesmayos: Sintoma;
  convulsiones: Sintoma;
  doloresCabeza: Sintoma;
  neuralgiaNeuritis: Sintoma;
  depresion: Sintoma;
  trastornosOculares: Sintoma;
  supuracionOidos: Sintoma;
  resfriosFrecuentes: Sintoma;
  tosCronica: Sintoma;
  sudoresNocturnos: Sintoma;
  perdidaPeso: Sintoma;
  doloresPecho: Sintoma;
  temblores: Sintoma;
  debilidadMuscular: Sintoma;
  faltaAire: Sintoma;
  palpitaciones: Sintoma;
  acidez: Sintoma;
  dificultadOrinar: Sintoma;
  sangreOrina: Sintoma;
  articulacionesHinchadas: Sintoma;
  lumbalgia: Sintoma;
  ciatalgia: Sintoma;
  desviacionColumna: Sintoma;
  fracturas: Sintoma;
  traumatismos: Sintoma;
  cirugias: Sintoma;
  internaciones: Sintoma;
  otros: Sintoma;
}

export interface CirugiaItem {
  tiene: boolean;
  cual?: string;
}

export interface AntecedentesQuirurgicos {
  traumatologica: CirugiaItem;
  cardiaca: CirugiaItem;
  ginecologica: CirugiaItem;
  general: CirugiaItem;
  neuroquirurgicas: CirugiaItem;
  malformacionesNacimiento: CirugiaItem;   // "de Nacimiento (Espina bífida, teratomas, etc)"
  hidrocefalia: CirugiaItem;
  malformacionesGeneticas: CirugiaItem;     // "por malformaciones genéticas de nacimiento (Síndrome de Down, Turner, etc)"
}
